import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  Anchor,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle,
  Clock,
  Compass,
  GithubLogo,
  GlobeSimple,
  LinkedinLogo,
  LinkSimple,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { usePostHog } from "@posthog/react";
import { api, type NewApplication } from "./api";
import { AutterWordmark, CaptainPatchImage } from "./components/brand/AutterBrand";

type HarbourSnapshot = {
  claimed?: string | null;
  crew?: unknown[];
  visited?: unknown[];
  collectedRules?: unknown[];
};

type FormValues = {
  name: string;
  email: string;
  linkedinUrl: string;
  githubUrl: string;
  workUrl: string;
  currentStage: string;
  shipped: string;
  shippedUrl: string;
  need: string;
  basedIn: string;
  timezone: string;
};

type FieldErrors = Partial<Record<keyof FormValues, string>>;
type SubmitState = "idle" | "sending" | "success" | "error";

const TOTAL_STEPS = 4;
const STEP_LABELS = ["Captain", "Proof", "Signal", "Waters"];
const CURRENT_STAGES = [
  { value: "exploring", label: "Exploring", detail: "Validating the problem" },
  { value: "building", label: "Building", detail: "Prototype or MVP" },
  { value: "launched", label: "Launched", detail: "Live with early users" },
  { value: "revenue", label: "Revenue", detail: "Paying customers" },
  { value: "scaling", label: "Scaling", detail: "Growing team or usage" },
] as const;
const TIMEZONES = [
  "America/Los_Angeles",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Australia/Sydney",
];

function normaliseUrl(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function validUrl(value: string) {
  try {
    const url = new URL(normaliseUrl(value));
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function readVoyage(): HarbourSnapshot {
  try {
    return JSON.parse(localStorage.getItem("same-boat-harbour-v1") || "{}") as HarbourSnapshot;
  } catch {
    return {};
  }
}

function fieldClass(hasError = false) {
  return `mt-2 w-full border-0 border-b-2 bg-transparent px-0 py-3 text-[1.05rem] text-ink outline-none transition-colors placeholder:text-muted/55 ${
    hasError ? "border-signal-deep" : "border-line focus:border-ink"
  }`;
}

function FieldError({ children }: { children?: string }) {
  if (!children) return null;
  return <p className="mt-2 font-mono text-[11px] text-signal-deep">{children}</p>;
}

function ApplicationPage({ onBack, onSail }: { onBack: () => void; onSail: () => void }) {
  const prefersReduced = useReducedMotion() ?? false;
  const posthog = usePostHog();
  const detectedTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata",
    [],
  );
  const voyage = useMemo(readVoyage, []);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [receipt, setReceipt] = useState("");
  const [values, setValues] = useState<FormValues>({
    name: "",
    email: "",
    linkedinUrl: "",
    githubUrl: "",
    workUrl: "",
    currentStage: "",
    shipped: "",
    shippedUrl: "",
    need: "",
    basedIn: "",
    timezone: detectedTimezone,
  });
  const headingRef = useRef<HTMLHeadingElement>(null);

  const vessel = typeof voyage.claimed === "string" ? voyage.claimed : "Uncharted";
  const crewCount = Array.isArray(voyage.crew) ? voyage.crew.length + 1 : 1;
  const boatsVisited = Array.isArray(voyage.visited) ? voyage.visited.length : 0;
  const harbourRules = Array.isArray(voyage.collectedRules) ? voyage.collectedRules.length : 0;

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, [step, submitState]);

  const setValue = (field: keyof FormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    if (submitState === "error") setSubmitState("idle");
  };

  const validateStep = (currentStep: number) => {
    const nextErrors: FieldErrors = {};
    if (currentStep === 1) {
      if (values.name.trim().length < 2) nextErrors.name = "Add the name we should call across the dock.";
      if (!/^\S+@\S+\.\S+$/.test(values.email.trim())) nextErrors.email = "Add a valid email so we can reply.";
      const links = [values.linkedinUrl, values.githubUrl, values.workUrl];
      if (!links.some((link) => link.trim())) {
        nextErrors.workUrl = "Share at least one place where we can see you or your work.";
      }
      if (values.linkedinUrl && !validUrl(values.linkedinUrl)) nextErrors.linkedinUrl = "Add a valid LinkedIn URL.";
      if (values.githubUrl && !validUrl(values.githubUrl)) nextErrors.githubUrl = "Add a valid GitHub URL.";
      if (values.workUrl && !validUrl(values.workUrl)) nextErrors.workUrl = "Add a valid portfolio or live project URL.";
    }
    if (currentStep === 2) {
      if (!values.currentStage) nextErrors.currentStage = "Choose the stage that best describes where you are today.";
      if (values.shipped.trim().length < 20) nextErrors.shipped = "Give us one concrete shipment in at least a sentence.";
      if (values.shippedUrl && !validUrl(values.shippedUrl)) nextErrors.shippedUrl = "This link does not look seaworthy yet.";
    }
    if (currentStep === 3 && values.need.trim().length < 12) {
      nextErrors.need = "Make the ask specific enough for another builder to help.";
    }
    if (currentStep === 4) {
      if (values.basedIn.trim().length < 2) nextErrors.basedIn = "Tell us which city or region you call home.";
      if (!values.timezone) nextErrors.timezone = "Choose your home waters.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const advance = () => {
    if (!validateStep(step)) return;
    posthog?.capture("application_step_completed", {
      step,
      step_label: STEP_LABELS[step - 1],
    });
    setStep((current) => Math.min(TOTAL_STEPS, current + 1));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (step < TOTAL_STEPS) {
      advance();
      return;
    }
    if (!validateStep(step) || submitState === "sending") return;

    const application: NewApplication = {
      name: values.name.trim(),
      email: values.email.trim().toLowerCase(),
      linkedinUrl: values.linkedinUrl ? normaliseUrl(values.linkedinUrl) : "",
      githubUrl: values.githubUrl ? normaliseUrl(values.githubUrl) : "",
      workUrl: values.workUrl ? normaliseUrl(values.workUrl) : "",
      currentStage: values.currentStage as NewApplication["currentStage"],
      shipped: values.shipped.trim(),
      shippedUrl: values.shippedUrl ? normaliseUrl(values.shippedUrl) : "",
      need: values.need.trim(),
      basedIn: values.basedIn.trim(),
      timezone: values.timezone,
      vessel,
      crewCount,
      boatsVisited,
      harbourRules,
    };

    setSubmitState("sending");
    try {
      const result = await api.createApplication(application);
      posthog?.identify(application.email, {
        email: application.email,
        name: application.name,
      });
      posthog?.capture("application_submitted", {
        application_id: result.id,
        current_stage: application.currentStage,
        timezone: application.timezone,
        vessel,
        crew_count: crewCount,
        boats_visited: boatsVisited,
        harbour_rules: harbourRules,
      });
      setReceipt(result.id.slice(0, 8).toUpperCase());
      setSubmitState("success");
    } catch {
      posthog?.capture("application_submit_failed");
      setSubmitState("error");
    }
  };

  return (
    <div className="application-page min-h-[100svh] bg-paper text-ink">
      <header className="relative z-30 h-16 border-b border-line bg-foam/95 backdrop-blur-md md:h-[4.5rem]">
        <div className="mx-auto flex h-full max-w-[1680px] items-center justify-between px-4 md:px-7">
          <button type="button" onClick={onBack} className="flex items-center gap-3" aria-label="Back to the Same Boat story">
            <AutterWordmark className="h-6 w-auto md:h-7" />
            <span className="h-5 w-px bg-line" aria-hidden />
            <span className="hidden font-mono text-[10px] tracking-[0.15em] uppercase sm:inline">Same Boat</span>
          </button>
          <div className="flex items-center gap-3">
            <span className="hidden items-center gap-2 font-mono text-[10px] tracking-[0.12em] text-muted uppercase sm:flex">
              <Clock className="size-4" aria-hidden />
              Under 3 minutes
            </span>
            <button
              type="button"
              onClick={onSail}
              className="inline-flex min-h-10 items-center gap-2 rounded-full border border-ink px-4 text-xs font-semibold transition-colors hover:bg-ink hover:text-foam"
            >
              <Compass className="size-4" weight="bold" aria-hidden />
              <span className="sm:hidden">Water</span>
              <span className="hidden sm:inline">Back to the water</span>
            </button>
          </div>
        </div>
      </header>

      <main className="grid min-h-[calc(100svh-4rem)] md:min-h-[calc(100svh-4.5rem)] lg:grid-cols-[minmax(22rem,0.88fr)_minmax(35rem,1.12fr)]">
        <aside className="relative min-h-[24rem] overflow-hidden border-b-2 border-ink sm:min-h-[30rem] lg:min-h-0 lg:border-r-2 lg:border-b-0">
          <img
            src="/page-assets/harbour-voyage.jpg"
            alt="An illustrated harbour where builders gather around a long table"
            className="absolute inset-0 size-full object-cover object-[54%_center]"
          />
          <div className="absolute top-5 left-5 flex items-center gap-2 border border-ink bg-foam px-3 py-2 font-mono text-[10px] tracking-[0.12em] uppercase shadow-card md:top-7 md:left-7">
            <Anchor className="size-4 text-signal-deep" weight="bold" aria-hidden />
            Harbour office · berth requests
          </div>
          <div className="absolute inset-x-4 bottom-4 border-2 border-ink bg-ink/94 p-5 text-foam shadow-lift md:inset-x-7 md:bottom-7 md:p-7">
            <p className="font-mono text-[10px] tracking-[0.16em] text-signal uppercase">A room for practitioners</p>
            <h1 className="mt-2 max-w-xl text-[clamp(2.4rem,5vw,4.6rem)] leading-[0.9] font-black tracking-[-0.045em] uppercase">
              Bring what you’ve shipped.
            </h1>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-night-text md:text-base">
              Not a founder network. Not an audience. Six builders at a table, helping with the work that is real right now.
            </p>
          </div>
        </aside>

        <section className="chart-grid relative flex min-w-0 items-start justify-center px-4 py-8 sm:px-8 md:py-10 xl:px-14">
          <div className="w-full max-w-[46rem]">
            <div className="mb-7 flex items-start justify-between gap-5 border-b border-line pb-5">
              <div>
                <p className="font-mono text-[10px] tracking-[0.16em] text-signal-deep uppercase">Application · entry manifest</p>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-ink-soft">
                  We read every application ourselves. This gets you a call. The call does the real vetting.
                </p>
              </div>
              <span className="shrink-0 font-mono text-sm font-semibold">{String(step).padStart(2, "0")} / 04</span>
            </div>

            <ol className="mb-8 grid grid-cols-4 gap-2" aria-label="Application progress">
              {STEP_LABELS.map((label, index) => {
                const number = index + 1;
                const complete = number < step || submitState === "success";
                const active = number === step && submitState !== "success";
                return (
                  <li key={label} aria-current={active ? "step" : undefined}>
                    <div className={`h-1 ${complete || active ? "bg-signal" : "bg-line"}`} />
                    <span className={`mt-2 block font-mono text-[9px] tracking-[0.08em] uppercase ${active ? "text-ink" : "text-muted"}`}>
                      {complete ? <Check className="mr-1 inline size-3" weight="bold" aria-hidden /> : `${number}. `}{label}
                    </span>
                  </li>
                );
              })}
            </ol>

            <AnimatePresence mode="wait">
              {submitState === "success" ? (
                <motion.div
                  key="success"
                  initial={prefersReduced ? false : { opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="relative border-2 border-ink bg-foam p-7 shadow-lift sm:p-10"
                >
                  <CheckCircle className="size-12 text-signal-deep" weight="fill" aria-hidden />
                  <p className="mt-7 font-mono text-[10px] tracking-[0.16em] text-signal-deep uppercase">Manifest received · {receipt}</p>
                  <h2 ref={headingRef} tabIndex={-1} className="mt-3 text-[clamp(2.2rem,6vw,4rem)] leading-[0.92] font-black tracking-[-0.04em] uppercase outline-none">
                    Your signal reached shore.
                  </h2>
                  <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft">
                    Sagnik and Tanvi will read it themselves. If the fit looks right, the next step is a 30-minute call, not another form.
                  </p>
                  <button type="button" onClick={onSail} className="mt-8 inline-flex h-12 items-center gap-2 rounded-full bg-ink px-6 font-semibold text-foam hover:bg-ink-soft">
                    Return to the harbour <Compass className="size-5" weight="bold" aria-hidden />
                  </button>
                  <CaptainPatchImage pose="welcome" decorative className="pointer-events-none absolute right-2 bottom-0 hidden w-40 translate-y-8 sm:block" />
                </motion.div>
              ) : (
                <motion.form
                  key={step}
                  onSubmit={submit}
                  noValidate
                  initial={prefersReduced ? false : { opacity: 0, x: 18 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={prefersReduced ? { opacity: 0 } : { opacity: 0, x: -14 }}
                  transition={{ duration: 0.22 }}
                  className="border-2 border-ink bg-foam p-6 shadow-lift sm:p-9"
                >
                  {step === 1 && (
                    <>
                      <p className="font-mono text-[10px] tracking-[0.14em] text-muted uppercase">Entry 01 · the captain</p>
                      <h2 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl font-extrabold tracking-[-0.025em] outline-none sm:text-4xl">Who is coming aboard?</h2>
                      <p className="mt-3 text-sm leading-relaxed text-ink-soft">Show us who you are and what you have built—past work, current work, and public profiles all count. Share at least one link.</p>
                      <div className="mt-8">
                        <label htmlFor="name" className="font-mono text-xs font-semibold tracking-[0.08em] uppercase">Your name</label>
                        <input id="name" name="name" autoComplete="name" value={values.name} onChange={(e) => setValue("name", e.target.value)} placeholder="What should we call you?" className={fieldClass(Boolean(errors.name))} />
                        <FieldError>{errors.name}</FieldError>
                      </div>
                      <div className="mt-7">
                        <label htmlFor="email" className="font-mono text-xs font-semibold tracking-[0.08em] uppercase">Email address</label>
                        <input id="email" name="email" type="email" autoComplete="email" value={values.email} onChange={(e) => setValue("email", e.target.value)} placeholder="you@company.com" className={fieldClass(Boolean(errors.email))} />
                        <p className="mt-2 font-mono text-[10px] leading-relaxed text-muted">Only used to contact you about your application.</p>
                        <FieldError>{errors.email}</FieldError>
                      </div>
                      <div className="mt-7 grid gap-6 sm:grid-cols-2">
                        <div>
                          <label htmlFor="linkedinUrl" className="flex items-center gap-2 font-mono text-xs font-semibold tracking-[0.08em] uppercase"><LinkedinLogo className="size-4" weight="bold" aria-hidden /> LinkedIn</label>
                          <input id="linkedinUrl" name="linkedinUrl" inputMode="url" autoComplete="url" value={values.linkedinUrl} onChange={(e) => setValue("linkedinUrl", e.target.value)} onBlur={() => values.linkedinUrl && setValue("linkedinUrl", normaliseUrl(values.linkedinUrl))} placeholder="linkedin.com/in/you" className={fieldClass(Boolean(errors.linkedinUrl))} />
                          <FieldError>{errors.linkedinUrl}</FieldError>
                        </div>
                        <div>
                          <label htmlFor="githubUrl" className="flex items-center gap-2 font-mono text-xs font-semibold tracking-[0.08em] uppercase"><GithubLogo className="size-4" weight="bold" aria-hidden /> GitHub</label>
                          <input id="githubUrl" name="githubUrl" inputMode="url" autoComplete="url" value={values.githubUrl} onChange={(e) => setValue("githubUrl", e.target.value)} onBlur={() => values.githubUrl && setValue("githubUrl", normaliseUrl(values.githubUrl))} placeholder="github.com/you" className={fieldClass(Boolean(errors.githubUrl))} />
                          <FieldError>{errors.githubUrl}</FieldError>
                        </div>
                      </div>
                      <div className="mt-7">
                        <label htmlFor="workUrl" className="flex items-center gap-2 font-mono text-xs font-semibold tracking-[0.08em] uppercase"><GlobeSimple className="size-4" weight="bold" aria-hidden /> Current or past work</label>
                        <input id="workUrl" name="workUrl" inputMode="url" autoComplete="url" value={values.workUrl} onChange={(e) => setValue("workUrl", e.target.value)} onBlur={() => values.workUrl && setValue("workUrl", normaliseUrl(values.workUrl))} placeholder="Your portfolio, live product, demo, or case study" className={fieldClass(Boolean(errors.workUrl))} />
                        <p className="mt-2 flex items-center gap-1.5 font-mono text-[10px] leading-relaxed text-muted"><LinkSimple className="size-3.5 shrink-0" aria-hidden /> A live link helps us understand what you actually ship.</p>
                        <FieldError>{errors.workUrl}</FieldError>
                      </div>
                    </>
                  )}

                  {step === 2 && (
                    <>
                      <p className="font-mono text-[10px] tracking-[0.14em] text-muted uppercase">Entry 02 · proof of work</p>
                      <h2 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl font-extrabold tracking-[-0.025em] outline-none sm:text-4xl">What have you shipped with agents?</h2>
                      <p className="mt-3 text-sm leading-relaxed text-ink-soft">Something from the last 90 days. Two sentences max. Specific beats polished.</p>
                      <fieldset className="mt-8">
                        <legend className="font-mono text-xs font-semibold tracking-[0.08em] uppercase">What stage are you at right now?</legend>
                        <div className="mt-3 grid gap-2 sm:grid-cols-2">
                          {CURRENT_STAGES.map(({ value, label, detail }) => {
                            const selected = values.currentStage === value;
                            return (
                              <label key={value} className={`cursor-pointer border-2 px-4 py-3 transition-colors ${selected ? "border-ink bg-ink text-foam" : "border-line bg-paper hover:border-ink"}`}>
                                <input type="radio" name="currentStage" value={value} checked={selected} onChange={(event) => setValue("currentStage", event.target.value)} className="sr-only" />
                                <span className="block text-sm font-semibold">{label}</span>
                                <span className={`mt-0.5 block font-mono text-[10px] ${selected ? "text-night-text" : "text-muted"}`}>{detail}</span>
                              </label>
                            );
                          })}
                        </div>
                        <FieldError>{errors.currentStage}</FieldError>
                      </fieldset>
                      <div className="mt-8">
                        <label htmlFor="shipped" className="font-mono text-xs font-semibold tracking-[0.08em] uppercase">The shipment</label>
                        <textarea id="shipped" name="shipped" rows={4} maxLength={420} value={values.shipped} onChange={(e) => setValue("shipped", e.target.value)} placeholder="We used agents to… It is now used by…" className={`${fieldClass(Boolean(errors.shipped))} resize-none leading-relaxed`} />
                        <div className="flex justify-between gap-4"><FieldError>{errors.shipped}</FieldError><span className="mt-2 ml-auto font-mono text-[10px] text-muted">{values.shipped.length}/420</span></div>
                      </div>
                      <div className="mt-6">
                        <label htmlFor="shippedUrl" className="font-mono text-xs font-semibold tracking-[0.08em] uppercase">Live link <span className="font-normal text-muted">· optional</span></label>
                        <input id="shippedUrl" name="shippedUrl" inputMode="url" value={values.shippedUrl} onChange={(e) => setValue("shippedUrl", e.target.value)} onBlur={() => values.shippedUrl && setValue("shippedUrl", normaliseUrl(values.shippedUrl))} placeholder="https://" className={fieldClass(Boolean(errors.shippedUrl))} />
                        <FieldError>{errors.shippedUrl}</FieldError>
                      </div>
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <p className="font-mono text-[10px] tracking-[0.14em] text-muted uppercase">Entry 03 · signal flare</p>
                      <h2 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl font-extrabold tracking-[-0.025em] outline-none sm:text-4xl">What do you need right now?</h2>
                      <p className="mt-3 text-sm leading-relaxed text-ink-soft">An intro, an answer, a customer, or a hire. Make it concrete enough that another builder can act.</p>
                      <div className="mt-8">
                        <label htmlFor="need" className="font-mono text-xs font-semibold tracking-[0.08em] uppercase">One specific ask</label>
                        <textarea id="need" name="need" rows={5} maxLength={280} value={values.need} onChange={(e) => setValue("need", e.target.value)} placeholder="I need an introduction to… because…" className={`${fieldClass(Boolean(errors.need))} resize-none leading-relaxed`} />
                        <div className="flex justify-between gap-4"><FieldError>{errors.need}</FieldError><span className="mt-2 ml-auto font-mono text-[10px] text-muted">{values.need.length}/280</span></div>
                      </div>
                      <blockquote className="mt-8 border-l-2 border-signal bg-paper-deep px-4 py-3 font-hand text-xl leading-snug text-ink-soft">
                        “The harbour does not promise calm water. It gives you people who know how to cross it.”
                      </blockquote>
                    </>
                  )}

                  {step === 4 && (
                    <>
                      <p className="font-mono text-[10px] tracking-[0.14em] text-muted uppercase">Entry 04 · home waters</p>
                      <h2 ref={headingRef} tabIndex={-1} className="mt-2 text-3xl font-extrabold tracking-[-0.025em] outline-none sm:text-4xl">Where are you based?</h2>
                      <p className="mt-3 text-sm leading-relaxed text-ink-soft">Tell us where you call home. Tables are matched by problem and timezone, not limited by city.</p>
                      <div className="mt-8">
                        <label htmlFor="basedIn" className="font-mono text-xs font-semibold tracking-[0.08em] uppercase">City or region, country</label>
                        <input id="basedIn" name="basedIn" autoComplete="address-level2" value={values.basedIn} onChange={(e) => setValue("basedIn", e.target.value)} placeholder="Bengaluru, India" className={fieldClass(Boolean(errors.basedIn))} />
                        <FieldError>{errors.basedIn}</FieldError>
                      </div>
                      <div className="mt-7">
                        <label htmlFor="timezone" className="font-mono text-xs font-semibold tracking-[0.08em] uppercase">Timezone</label>
                        <select id="timezone" name="timezone" value={values.timezone} onChange={(e) => setValue("timezone", e.target.value)} className={`${fieldClass(Boolean(errors.timezone))} cursor-pointer`}>
                          {[detectedTimezone, ...TIMEZONES.filter((zone) => zone !== detectedTimezone)].map((zone) => <option key={zone} value={zone}>{zone.replaceAll("_", " ")}</option>)}
                        </select>
                        <FieldError>{errors.timezone}</FieldError>
                      </div>
                      <div className="mt-8 grid grid-cols-3 border-y border-line py-4 text-center">
                        <div><span className="block font-mono text-[9px] tracking-[0.1em] text-muted uppercase">Vessel</span><strong className="mt-1 block truncate text-sm">{vessel}</strong></div>
                        <div className="border-x border-line"><span className="block font-mono text-[9px] tracking-[0.1em] text-muted uppercase">Crew</span><strong className="mt-1 block text-sm">{crewCount}</strong></div>
                        <div><span className="block font-mono text-[9px] tracking-[0.1em] text-muted uppercase">Visited</span><strong className="mt-1 block text-sm">{boatsVisited}/8</strong></div>
                      </div>
                      <p className="mt-5 flex items-start gap-2 text-xs leading-relaxed text-muted"><CheckCircle className="mt-0.5 size-4 shrink-0 text-signal-deep" weight="fill" aria-hidden /> Seats are few. Every application is read by Sagnik and Tanvi, and the next step is a 30-minute screening call.</p>
                    </>
                  )}

                  {submitState === "error" && (
                    <p role="alert" className="mt-6 border border-signal-deep bg-paper-deep px-4 py-3 text-sm text-ink">
                      The harbour signal did not send. Check your connection and try once more.
                    </p>
                  )}

                  <div className="mt-9 flex items-center justify-between gap-4 border-t border-line pt-6">
                    <button
                      type="button"
                      onClick={step === 1 ? onBack : () => setStep((current) => Math.max(1, current - 1))}
                      className="inline-flex min-h-11 items-center gap-2 font-mono text-xs text-muted underline-offset-4 hover:text-ink hover:underline"
                    >
                      <ArrowLeft className="size-4" weight="bold" aria-hidden /> {step === 1 ? "Leave the office" : "Previous entry"}
                    </button>
                    <button
                      type="submit"
                      disabled={submitState === "sending"}
                      className="group inline-flex h-12 items-center gap-2 rounded-full bg-signal px-6 font-semibold text-ink transition-transform hover:-translate-y-0.5 hover:bg-signal-deep hover:text-foam disabled:cursor-wait disabled:opacity-65 motion-reduce:hover:translate-y-0"
                    >
                      {step === TOTAL_STEPS ? (submitState === "sending" ? "Sending manifest…" : "Request a berth") : "Next entry"}
                      {step === TOTAL_STEPS ? <PaperPlaneTilt className="size-5" weight="bold" aria-hidden /> : <ArrowRight className="size-5 transition-transform group-hover:translate-x-0.5" weight="bold" aria-hidden />}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] tracking-[0.05em] text-muted">
              <span>Different work. Shared water.</span>
              <span>No pitch decks required.</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export { ApplicationPage };
